import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck } from "lucide-react";

const TIER_FEATURES = {
  gap_only: ["Full SPRS score", "All objectives assessed", "Auto POA&M", "Cited evidence"],
  readiness: ["Everything in Gap Only", "40+ AI policies", "Evidence Collector", "Quarterly review"],
  audit_concierge: ["Everything in Readiness", "Monthly expert review", "C3PAO prep & liaison", "Priority support"],
};

export default function Billing() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/billing/status").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 10) { toast.error("Payment status timed out. Refresh to check."); return; }
    try {
      const { data: s } = await api.get(`/billing/status/${sessionId}`);
      if (s.payment_status === "paid") {
        toast.success("Subscription active — features unlocked!");
        setParams({}, { replace: true });
        load();
        return;
      }
      if (s.status === "expired") { toast.error("Payment session expired."); setParams({}, { replace: true }); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2500);
    } catch (e) { toast.error("Could not verify payment."); }
  }, [load, setParams]);

  const pollPaypal = useCallback(async (subId, attempts = 0) => {
    if (attempts >= 12) { toast.error("PayPal status timed out. Refresh to check."); return; }
    try {
      const { data: s } = await api.get(`/paypal/status/${subId}`);
      if (s.payment_status === "paid") {
        toast.success("PayPal subscription active — features unlocked!");
        setParams({}, { replace: true });
        load();
        return;
      }
      setTimeout(() => pollPaypal(subId, attempts + 1), 2500);
    } catch (e) { toast.error("Could not verify PayPal payment."); }
  }, [load, setParams]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { toast.message("Verifying payment…"); pollStatus(sid); }
    const ppSub = params.get("subscription_id");
    if (params.get("paypal") && ppSub) { toast.message("Verifying PayPal…"); pollPaypal(ppSub); }
  }, [params, pollStatus, pollPaypal]);

  const subscribe = async (packageId) => {
    setBusy(packageId);
    try {
      const { data: res } = await api.post("/billing/checkout", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setBusy(null); }
  };

  const subscribePaypal = async (packageId) => {
    setBusy(`pp-${packageId}`);
    try {
      const { data: res } = await api.post("/paypal/checkout", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) { toast.error(e.response?.data?.detail || "PayPal checkout failed"); setBusy(null); }
  };

  const manage = async () => {
    setBusy("PORTAL");
    try {
      const { data: res } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open portal"); setBusy(null); }
  };

  const cancelPaypal = async () => {
    if (!window.confirm("Cancel your PayPal subscription? Paid features will lock immediately.")) return;
    setBusy("PP-CANCEL");
    try {
      await api.post("/paypal/cancel");
      toast.success("PayPal subscription cancelled.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">Billing</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Subscription</h1>
          <p className="text-slate-500 text-sm mt-1">
            {data.active
              ? <>Current plan: <span className="font-semibold text-brand">{data.packages.find((p) => p.id === data.tier)?.name || data.tier}</span> · active</>
              : "No active plan. Subscribe to unlock the Policy Generator and Evidence Collector."}
          </p>
        </div>
        {data.active && (
          <ManageButton data={data} busy={busy} onManage={manage} onCancel={cancelPaypal} />
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {data.packages.map((p) => (
          <TierCard key={p.id} p={p} data={data} busy={busy}
            onSubscribe={subscribe} onSubscribePaypal={subscribePaypal} />
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-6">Test mode — card: Stripe test card 4242 4242 4242 4242 (any future expiry & CVC). PayPal: use a sandbox buyer account to approve.</p>
    </div>
  );
}

function ManageButton({ data, busy, onManage, onCancel }) {
  if (data.provider === "paypal")
    return (
      <button onClick={onCancel} disabled={busy === "PP-CANCEL"} data-testid="cancel-paypal-btn"
        className="border border-red-300 text-red-600 rounded-md px-5 py-2.5 text-sm font-medium hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50">
        {busy === "PP-CANCEL" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Cancel PayPal subscription
      </button>
    );
  return (
    <button onClick={onManage} disabled={busy === "PORTAL"} data-testid="manage-subscription-btn"
      className="border border-slate-300 rounded-md px-5 py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50">
      {busy === "PORTAL" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Manage subscription
    </button>
  );
}

function TierCard({ p, data, busy, onSubscribe, onSubscribePaypal }) {
  const isCurrent = data.active && data.tier === p.id;
  const featured = p.id === "readiness";
  return (
    <div data-testid={`billing-tier-${p.id}`}
      className={`rounded-md p-7 bg-white border ${featured ? "border-brand-accent ring-2 ring-brand-accent/20 shadow-md" : "border-slate-200 shadow-sm"}`}>
      {featured && <div className="text-xs font-semibold text-brand-accent uppercase tracking-wider mb-2">Most popular</div>}
      <h3 className="font-display font-bold text-xl tracking-tight">{p.name}</h3>
      <div className="my-4"><span className="font-display font-black text-4xl">${p.amount.toLocaleString()}</span><span className="text-slate-500">/mo</span></div>
      <ul className="space-y-2.5 mb-6">
        {(TIER_FEATURES[p.id] || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /> {f}</li>
        ))}
      </ul>
      {isCurrent ? (
        <div className="flex items-center justify-center gap-2 py-2.5 rounded-md bg-success-bg text-success font-medium text-sm" data-testid={`current-${p.id}`}>
          <ShieldCheck className="h-4 w-4" /> Active plan
        </div>
      ) : (
        <div className="space-y-2">
          <button onClick={() => onSubscribe(p.id)} disabled={!!busy} data-testid={`subscribe-${p.id}`}
            className={`w-full py-2.5 rounded-md font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${featured ? "bg-brand text-white hover:bg-brand-secondary" : "border border-slate-300 hover:bg-slate-50"}`}>
            {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {data.active ? "Switch to this plan" : "Pay with card"}
          </button>
          {data.paypal_enabled && (
            <button onClick={() => onSubscribePaypal(p.id)} disabled={!!busy} data-testid={`subscribe-paypal-${p.id}`}
              className="w-full py-2.5 rounded-md font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 bg-[#ffc439] text-[#003087] hover:brightness-95">
              {busy === `pp-${p.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Pay with PayPal
            </button>
          )}
        </div>
      )}
    </div>
  );
}
